import type {
  Centre,
  CentreApprovalRequest,
  CentreStats,
  CentreStudent,
  CentreStudentSession,
  TelegramWidgetData,
} from '@ielts-buddy/shared'

const rawApiUrl = import.meta.env.VITE_API_URL ?? ''
const API_URL = rawApiUrl.trim().replace(/\/+$/, '')

const rawPathPrefix = import.meta.env.VITE_API_PATH_PREFIX
const API_PATH_PREFIX =
  typeof rawPathPrefix === 'string' && rawPathPrefix.trim().length > 0
    ? rawPathPrefix.trim().replace(/^\/+|\/+$/g, '')
    : ''

function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const prefix = API_PATH_PREFIX ? `/${API_PATH_PREFIX}` : ''
  return `${API_URL}${prefix}${normalized}`
}

const TOKEN_KEY = 'ielts-buddy:centre-token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  const token = getToken()
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(apiUrl(path), { ...init, headers })
  const isJson = res.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    const message =
      (isJson &&
      typeof payload === 'object' &&
      payload !== null &&
      'error' in payload
        ? String((payload as { error: unknown }).error)
        : null) ?? `Request failed with status ${res.status}`
    throw new ApiError(message, res.status, payload)
  }

  return payload as T
}

export async function centreLogin(
  data: TelegramWidgetData,
): Promise<{ centre: Centre; token: string }> {
  return request<{ centre: Centre; token: string }>('/centres/auth', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function fetchCentreMe(): Promise<{
  centre: Centre
  stats: CentreStats
}> {
  return request<{ centre: Centre; stats: CentreStats }>('/centres/me')
}

export async function fetchCentreStudents(): Promise<{
  students: CentreStudent[]
}> {
  return request<{ students: CentreStudent[] }>('/centres/me/students')
}

export async function fetchStudentSessions(
  userId: string,
): Promise<{ sessions: CentreStudentSession[] }> {
  return request<{ sessions: CentreStudentSession[] }>(
    `/centres/me/students/${userId}`,
  )
}

export async function fetchPendingCentreRequests(): Promise<{
  requests: CentreApprovalRequest[]
}> {
  return request<{ requests: CentreApprovalRequest[] }>(
    '/centres/admin/requests',
  )
}

export async function approveCentreRequest(centreId: string): Promise<void> {
  await request<{ ok: true }>(`/centres/admin/requests/${centreId}/approve`, {
    method: 'POST',
  })
}

export async function rejectCentreRequest(centreId: string): Promise<void> {
  await request<{ ok: true }>(`/centres/admin/requests/${centreId}/reject`, {
    method: 'POST',
  })
}
